/**
 * C2 — Helpers for rendering a booking's services list.
 *
 * Backend (B4) returns `booking.services: [{ serviceUuid, serviceName, ... }]`.
 * Legacy/older responses only have `booking.serviceName`. These helpers
 * tolerate both shapes so single-service bookings render unchanged.
 */

const DEFAULT_FALLBACK = 'Service';

/**
 * Returns a normalized list of service names for a booking.
 * Falls back to `[booking.serviceName]` when the multi-service array is absent.
 * Empty array if nothing resolvable.
 */
export function bookingServiceNames(booking) {
  if (!booking) return [];
  const services = Array.isArray(booking.services) ? booking.services : null;
  if (services && services.length > 0) {
    return services
      .map((s) => (s && s.serviceName ? String(s.serviceName) : null))
      .filter(Boolean);
  }
  if (booking.serviceName) {
    return [String(booking.serviceName)];
  }
  return [];
}

/**
 * Truncated display string suitable for a booking row.
 *   1 service       → "Haircut"
 *   2 services      → "Haircut, Beard Trim"
 *   3+ services     → "Haircut, Beard Trim (+1 more)"
 * Empty → fallback ("Service").
 */
export function formatBookingServices(booking, { fallback = DEFAULT_FALLBACK } = {}) {
  const names = bookingServiceNames(booking);
  if (names.length === 0) return fallback;
  if (names.length <= 2) return names.join(', ');
  const head = names.slice(0, 2).join(', ');
  const extra = names.length - 2;
  return `${head} (+${extra} more)`;
}

/**
 * Lower-cased searchable string for client-side filtering.
 * Joins every service name so a query against "massage" still hits a
 * multi-service booking where massage is the second line item.
 */
export function bookingServicesSearchText(booking) {
  return bookingServiceNames(booking).join(' ').toLowerCase();
}
